import { backendService } from "../../services/backend.service";
import { downloadBlob } from "../../utils/downloadBlob";
import { PbjSubmissionProcessType } from "../../utils/models";

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const ZIP_MIME = "application/zip";

// Mirrors the backend's `sanitize_for_filename` (services/pbj_model/__init__.py) so the saved
// file matches the server's Content-Disposition name without reading the header cross-origin:
// lowercase ASCII words joined by `-` (NFKD-decomposed so accented letters keep their base
// letter), capped at 60 chars. Shared by both the raw and submission exports.
const deptFilenameSlug = (displayName: string): string =>
	displayName
		.normalize("NFKD")
		.replace(/[^\x20-\x7e]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 60)
		.replace(/-+$/, "");

export type PbjExportRequest = {
	departmentId: string;
	/** The building's display name — it goes into the saved file's name. */
	buildingName: string;
	startDate: string;
	endDate: string;
};

/** Fetch and save one building's raw PBJ workbook. Rejects when the server refuses or the request fails. */
export const downloadRawExport = async ({ departmentId, buildingName, startDate, endDate }: PbjExportRequest): Promise<void> => {
	const res = await backendService.downloadPbjAuditRawExport(departmentId, startDate, endDate);
	const deptSlug = deptFilenameSlug(buildingName);
	const rangeLabel = `${startDate}-to-${endDate}`;
	// DEFAULT: a display name with no ASCII alphanumerics slugs to "" — degrade to the
	// dept-less legacy filename (same rule as the backend) instead of "PBJ-RAW--…".
	downloadBlob(res.data, deptSlug ? `PBJ-RAW-${deptSlug}-${rangeLabel}.xlsx` : `PBJ-RAW-${rangeLabel}.xlsx`, XLSX_MIME);
};

/** Fetch and save one building's CMS submission zip. Rejects when the server refuses (e.g. no facility ID) or the request fails. */
export const downloadSubmissionExport = async (request: PbjExportRequest, processType: PbjSubmissionProcessType): Promise<void> => {
	const { departmentId, buildingName, startDate, endDate } = request;
	const res = await backendService.downloadPbjAuditSubmissionExport(departmentId, startDate, endDate, processType);
	const deptSlug = deptFilenameSlug(buildingName);
	const rangeLabel = `${startDate}-to-${endDate}`;
	// Mirrors the backend's filename token (services/pbj_model/submission_export.py): merge keeps
	// the plain name, replace is tagged so the two variants can't be mixed up in a downloads folder.
	const processFilenameToken = processType === PbjSubmissionProcessType.Merge ? "" : `-${processType}`;
	// DEFAULT: a display name with no ASCII alphanumerics slugs to "" — degrade to the
	// dept-less legacy filename (same rule as the backend) instead of "pbj-submission--…".
	const filename = deptSlug ? `pbj-submission-${deptSlug}-${rangeLabel}${processFilenameToken}.zip` : `pbj-submission-${rangeLabel}${processFilenameToken}.zip`;
	downloadBlob(res.data, filename, ZIP_MIME);
};
